import { Directive, HostListener, output } from '@angular/core';

/**
 * Direttiva per gestire l'evento di rilascio (drop) dei file su un elemento specifico.
 * * @description
 * Questa direttiva intercetta le operazioni di drag and drop dell'utente,
 * prevenendo i comportamenti di default del browser (come l'apertura del file)
 * e catturando l'oggetto `FileList` per l'elaborazione.
 * * @usageNotes
 * Applicare la direttiva a un elemento HTML e ascoltare l'evento `fileDropped`:
 * ```html
 * <div appDragAndDropFiles (fileDropped)="handleMyFiles($event)"></div>
 * ```
 */
@Directive({
  selector: '[appDragAndDropFiles]',
})
export class DragAndDropFilesDirective {
  /**
   * Evento emesso quando l'utente rilascia i file sull'elemento host.
   * Restituisce un oggetto di tipo `FileList`.
   */
  fileDropped = output<FileList>();

  /**
   * Gestisce l'evento 'dragover'.
   * È indispensabile chiamare `preventDefault()` per permettere al browser
   * di considerare l'elemento come una zona di rilascio valida.
   * * @param evt L'evento di trascinamento.
   */
  @HostListener('dragover', ['$event'])
  onDragOver(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
  }

  /**
   * Gestisce il rilascio effettivo dei file (evento 'drop').
   * Estrae i file dall'oggetto `DataTransfer` ed emette l'evento verso il componente.
   * * @param evt L'evento di rilascio.
   */
  @HostListener('drop', ['$event'])
  onDrop(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();

    const files = evt.dataTransfer?.files;

    // Verifichiamo che l'oggetto files esista e contenga almeno un elemento
    if (files && files.length > 0) {
      this.fileDropped.emit(files);
    }
  }
}
